const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'https://search-web-quality-production.up.railway.app';

export function getApiKey(): string | null {
  return localStorage.getItem('admin_api_key');
}

export function setApiKey(key: string) {
  localStorage.setItem('admin_api_key', key);
}

export function clearApiKey() {
  localStorage.removeItem('admin_api_key');
}

async function fetchWithAuth<T = any>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const apiKey = getApiKey();
  if (!apiKey) {
    throw new Error('API key not found. Please configure it in Settings.');
  }

  const headers = new Headers(options.headers);
  headers.set('X-API-Key', apiKey);
  headers.set('Content-Type', 'application/json');

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (response.status === 401 || response.status === 403) {
    throw new Error('Unauthorized: Invalid or expired API key.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Request failed with status ${response.status}`);
  }

  return response.json() as Promise<T>;
}

export const api = {
  fetch: fetchWithAuth,
};

// --- Schemas & Types ---

export interface Business {
  id: string;
  name: string;
  place_id: string | null;
  website: string | null;
  lat: number | null;
  lng: number | null;
  primary_category: string | null;
  secondary_categories: string[];
  city: string | null;
  country_code: string | null;
  google_domain: string | null;
  hl: string | null;
  gl: string | null;
  gbp_verified: boolean | null;
  google_rating: number | null;
  google_review_count: number | null;
  created_at: string;
}

export interface Keyword {
  id: string;
  keyword_text: string;
  origin: string;
  intent: string | null;
  is_branded: boolean | null;
  is_local_intent: boolean | null;
  monthly_volume: number | null;
  created_at: string;
}

export interface Job {
  id: string;
  business_id: string;
  block: string;
  status: string;
  stage_detail: Record<string, any>;
  error: string | null;
  created_at: string;
  finished_at: string | null;
}

export interface LedgerItem {
  id: number;
  occurred_at: string;
  provider: string;
  operation: string;
  block: string | null;
  model: string | null;
  business_id: string | null;
  job_id: string | null;
  http_status: number | null;
  cache_hit: boolean;
  duration_ms: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  unit_cost_usd: number;
  request_meta: Record<string, any>;
}

export interface CostSummary {
  mtd_total_cost_usd: number;
  last_month_total_cost_usd: number;
  delta_pct: number | null;
  projected_month_end_usd: number;
  total_input_tokens: number;
  total_output_tokens: number;
  by_provider: { provider: string; calls: number; cost_usd: number; input_tokens: number; output_tokens: number }[];
  by_block: { block: string; calls: number; cost_usd: number }[];
}

export interface CostByBlockItem {
  block: string;
  total_calls: number;
  total_cache_hits: number;
  total_errors: number;
  total_cost_usd: number;
  avg_cost_per_call: number;
}

export interface HealthDetail {
  total_businesses: number;
  jobs_by_status: Record<string, number>;
  provider_calls_today: { provider: string; calls: number; cost_usd: number }[];
  budget_utilization: Record<string, { cap_usd: number; spent_usd: number; utilization_pct: number }>;
  redis_connected: boolean;
}

export interface ConfigValue {
  [key: string]: any;
}

export interface Prompt {
  key: string;
  block: string;
  version: number;
  content: string;
  is_active: boolean;
  updated_at: string;
}

export interface ApiKey {
  id: string;
  name: string;
  tier: string;
  key_value: string; // Only returned on creation
  created_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
}

// --- Specific API Endpoints ---

// 1. Auth / API Keys
export const createApiKey = (name: string, tier: 'public' | 'internal' | 'admin') => 
  api.fetch<{ id: string; name: string; tier: string; key_value: string }>('/v1/admin/keys', {
    method: 'POST',
    body: JSON.stringify({ name, tier }),
  });

export const listApiKeys = () => api.fetch<{ items: ApiKey[] }>('/v1/admin/keys');

export const revokeApiKey = (keyId: string) => 
  api.fetch(`/v1/admin/keys/${keyId}`, { method: 'DELETE' });

// 2. Businesses
export interface RegisterBusinessRequest {
  name: string;
  place_id?: string | null;
  website?: string | null;
  lat?: number | null;
  lng?: number | null;
  primary_category?: string | null;
  secondary_categories?: string[];
  city?: string | null;
  country_code?: string | null;
  google_domain?: string | null;
  hl?: string | null;
  gl?: string | null;
  gbp_verified?: boolean | null;
  google_rating?: number | null;
  google_review_count?: number | null;
}
export const listBusinesses = (page = 1, pageSize = 50, search?: string) => {
  const params = new URLSearchParams();
  params.append('page', String(page));
  params.append('page_size', String(pageSize));
  if (search) params.append('search', search);
  return api.fetch<{ total: number; page: number; page_size: number; items: Business[] }>(`/v1/businesses?${params.toString()}`);
};

export const registerBusiness = (payload: RegisterBusinessRequest) => 
  api.fetch<Business>('/v1/businesses', { method: 'POST', body: JSON.stringify(payload) });

export const getBusiness = (businessId: string) => 
  api.fetch<Business>(`/v1/businesses/${businessId}`);

export interface UpdateBusinessRequest {
  name?: string | null;
  website?: string | null;
  lat?: number | null;
  lng?: number | null;
  primary_category?: string | null;
  secondary_categories?: string[] | null;
  city?: string | null;
  country_code?: string | null;
  google_domain?: string | null;
  hl?: string | null;
  gl?: string | null;
  gbp_verified?: boolean | null;
  google_rating?: number | null;
  google_review_count?: number | null;
}
export const updateBusiness = (businessId: string, payload: UpdateBusinessRequest) => 
  api.fetch<Business>(`/v1/businesses/${businessId}`, { method: 'PUT', body: JSON.stringify(payload) });

export const deleteBusiness = (businessId: string) => 
  api.fetch(`/v1/admin/businesses/${businessId}`, { method: 'DELETE' });

// 3. Keywords
export const addTrackedKeywords = (businessId: string, keywords: string[]) => 
  api.fetch(`/v1/businesses/${businessId}/keywords`, { 
    method: 'POST', 
    body: JSON.stringify({ keywords }) 
  });

export const listKeywords = (businessId: string, origin?: string) => {
  const params = origin ? `?origin=${origin}` : '';
  return api.fetch<{ items: Keyword[] }>(`/v1/businesses/${businessId}/keywords${params}`);
};

export const deleteKeyword = (businessId: string, keywordId: string) => 
  api.fetch(`/v1/businesses/${businessId}/keywords/${keywordId}`, { method: 'DELETE' });

// 4. Blocks Refresh (Triggers)
export const refreshGridSearch = (businessId: string, keyword: string, grid_size = 3, radius_km = 3.0) => 
  api.fetch(`/v1/businesses/${businessId}/grid-search:refresh`, { 
    method: 'POST', 
    body: JSON.stringify({ keyword, grid_size, radius_km }) 
  });

export const refreshPerspectiveSearch = (businessId: string, payload: any) => 
  api.fetch(`/v1/businesses/${businessId}/perspective-search:refresh`, { 
    method: 'POST', 
    body: JSON.stringify(payload) 
  });

export const refreshKeywordSearch = (businessId: string) => 
  api.fetch(`/v1/businesses/${businessId}/keyword-search:refresh`, { method: 'POST' });

export const refreshKeywordGeneration = (businessId: string, validate_top = 0) => 
  api.fetch(`/v1/businesses/${businessId}/keyword-generation:refresh`, { 
    method: 'POST', 
    body: JSON.stringify({ validate_top }) 
  });

export const refreshSeoAudit = (businessId: string, page_cap = 50) => 
  api.fetch(`/v1/businesses/${businessId}/seo-audit:refresh`, { 
    method: 'POST', 
    body: JSON.stringify({ page_cap }) 
  });

export const refreshTechnicalHealth = (businessId: string, strategy: 'mobile' | 'desktop' = 'mobile') => 
  api.fetch(`/v1/businesses/${businessId}/technical-health:refresh?strategy=${strategy}`, { method: 'POST' });

export const refreshContentQuality = (businessId: string) => 
  api.fetch(`/v1/businesses/${businessId}/content-quality:refresh`, { method: 'POST' });

export const refreshAeoSignals = (businessId: string) => 
  api.fetch(`/v1/businesses/${businessId}/aeo-signals:refresh`, { method: 'POST' });

// 5. Results & Reads
export const getLatestSnapshot = (businessId: string, block: string) => 
  api.fetch(`/v1/businesses/${businessId}/blocks/${block}`);

export const getGridRun = (businessId: string, runId: string) => 
  api.fetch(`/v1/businesses/${businessId}/grid-search/${runId}`);

export const getBlockRecommendations = (businessId: string, block: string) => 
  api.fetch(`/v1/businesses/${businessId}/blocks/${block}/recommendations`);

export const getKeywordHistory = (businessId: string, keyword?: string, keyword_id?: string) => {
  const params = new URLSearchParams();
  if (keyword) params.append('keyword', keyword);
  if (keyword_id) params.append('keyword_id', keyword_id);
  return api.fetch(`/v1/businesses/${businessId}/keyword-search/history?${params.toString()}`);
};

export const getSources = (businessId: string, block?: string) => {
  const params = block ? `?block=${block}` : '';
  return api.fetch(`/v1/businesses/${businessId}/sources${params}`);
};

// 6. Jobs
export const getJob = (jobId: string) => api.fetch<Job>(`/v1/jobs/${jobId}`);

export const getJobs = (status?: string, block?: string, page = 1, pageSize = 50) => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (block) params.append('block', block);
  params.append('page', String(page));
  params.append('page_size', String(pageSize));
  return api.fetch<{ total: number; page: number; page_size: number; items: Job[] }>(`/v1/admin/jobs?${params.toString()}`);
};

export const retryJob = (jobId: string) => 
  api.fetch(`/v1/admin/jobs/${jobId}/retry`, { method: 'POST' });

// 7. Costs
export const getCostSummary = () => api.fetch<CostSummary>('/v1/admin/costs/summary');

export const getCostsByBlock = () => api.fetch<{ items: CostByBlockItem[] }>('/v1/admin/costs/by-block');

export const getCostsByBusiness = (period: 'day' | 'month' = 'month', limit = 50) => 
  api.fetch(`/v1/admin/costs/by-business?period=${period}&limit=${limit}`);

export const getCostsByProvider = (from?: string, to?: string) => {
  const params = new URLSearchParams();
  if (from) params.append('from', from);
  if (to) params.append('to', to);
  return api.fetch(`/v1/admin/costs/by-provider?${params.toString()}`);
};

export const getCostDaily = (from?: string, to?: string, provider?: string, block?: string) => {
  const params = new URLSearchParams();
  if (from) params.append('from', from);
  if (to) params.append('to', to);
  if (provider) params.append('provider', provider);
  if (block) params.append('block', block);
  return api.fetch(`/v1/admin/costs/daily?${params.toString()}`);
};

export const getCostLedger = (provider?: string, block?: string, page = 1, pageSize = 50) => {
  const params = new URLSearchParams();
  if (provider) params.append('provider', provider);
  if (block) params.append('block', block);
  params.append('page', String(page));
  params.append('page_size', String(pageSize));
  return api.fetch<{ total: number; page: number; page_size: number; items: LedgerItem[] }>(`/v1/admin/costs/ledger?${params.toString()}`);
};

export interface UpsertRateRequest {
  provider: string;
  operation: string;
  model?: string | null;
  cost_per_request_usd?: number | null;
  cost_per_1k_input_usd?: number | null;
  cost_per_1k_output_usd?: number | null;
  effective_from: string;
  note?: string | null;
}
export const listCostRates = () => api.fetch('/v1/admin/cost-rates');
export const upsertCostRate = (payload: UpsertRateRequest) => 
  api.fetch('/v1/admin/cost-rates', { method: 'PUT', body: JSON.stringify(payload) });

export const getBudgets = () => api.fetch('/v1/admin/budgets');
export const setBudgets = (budgets: Record<string, number>) => 
  api.fetch('/v1/admin/budgets', { method: 'PUT', body: JSON.stringify({ budgets }) });

// 8. Configuration & Prompts
export const getConfig = (key: 'grid' | 'scoring' | 'keywords') => 
  api.fetch<ConfigValue>(`/v1/admin/config/${key}`);

export const setConfig = (key: 'grid' | 'scoring' | 'keywords', value: ConfigValue) => 
  api.fetch(`/v1/admin/config/${key}`, { method: 'PUT', body: JSON.stringify(value) });

export const getPrompts = () => api.fetch<{ items: Prompt[] }>('/v1/admin/prompts');

export const createPrompt = (block: string, content: string) => 
  api.fetch('/v1/admin/prompts', { 
    method: 'POST', 
    body: JSON.stringify({ block, content }) 
  });

export const activatePrompt = (promptKey: string) => 
  api.fetch(`/v1/admin/prompts/${promptKey}:activate`, { method: 'POST' });

// 9. Admin Operations
export const recomputeBlock = (businessId: string, block: string) => 
  api.fetch(`/v1/admin/businesses/${businessId}/blocks/${block}:recompute`, { method: 'POST' });

export const getHealthDetail = () => api.fetch<HealthDetail>('/v1/admin/health/detail');
